import {
    alpha,
    colors,
    CSSObject,
    filledInputClasses,
    formHelperTextClasses,
    inputBaseClasses,
    inputLabelClasses,
    outlinedInputClasses,
    TextFieldProps,
    Theme,
    styled,
    tooltipClasses,
    stackClasses,
} from "@mui/material"
import { Tooltip } from "../../DataDisplay/tooltip"

export function getTextFieldStyles({ theme, size, hiddenLabel }: TextFieldProps & { theme: Theme }): CSSObject {
    return {
        boxSizing: "border-box",

        // Display error Helpertext
        "&:hover": {
            [`.${formHelperTextClasses.root}`]: {
                [`&.${formHelperTextClasses.error}`]: {
                    display: "block",
                },
            },

            [` .${stackClasses.root}`]: {
                visibility: "visible",
            },
        },

        // Counter
        "& .inputCounter": {
            position: "absolute",
            top: "-17px",
            fontSize: "10px",
            right: "6px",

            // Size small
            ...(size === "small" && {
                top: "-19px",
            }),

            // Size Large
            ...(size === "large" && {
                top: "-5px",
            }),

            // Size Extralarge
            ...(size === "extralarge" && {
                top: "-2px",
            }),
        },

        // Close Icon, EndAdornment (only for TextField.tsx)
        "& .inputButtons": {
            right: "5px",
            padding: "4px",
        },

        // Styling EndAdornment
        ".MuiInputAdornment-root": {
            marginTop: 0,
            marginLeft: 0,
            gap: theme.spacing(0.5),

            // Size Large
            ...(size === "large" && {
                marginTop: "8px",
            }),

            // Size Extralarge
            ...(size === "extralarge" && {
                marginTop: "14px",
            }),
        },

        // Label
        [`.${inputLabelClasses.filled}`]: {
            fontSize: "10px",
            transform: "translate(4px,-14px) scale(1)",
            textTransform: "uppercase",
            opacity: 0.54,

            "&.Mui-focused, &.MuiFormLabel-filled": {
                transform: "translate(4px,-14px) scale(1)",
            },

            // Size small
            ...(size === "small" && {
                fontSize: "9px",
                transform: "translate(2px,-12px) scale(1)",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                    transform: "translate(2px,-12px) scale(1)",
                },
            }),

            // Size Large
            ...(size === "large" && {
                fontSize: "10px",
                transform: "translate(9px,2px) scale(0.87)",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                    transform: "translate(9px,2px) scale(0.87)",
                },
            }),

            // Size Extralarge
            ...(size === "extralarge" && {
                fontSize: "10px",
                transform: "translate(12px,3px) scale(1)",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                    transform: "translate(12px,3px) scale(1)",
                },
            }),
        },

        // Input
        [`.${filledInputClasses.input}`]: {
            background: "none",

            // Size Medium
            padding: "8px 9px",
            fontSize: theme.overwrites?.components?.textfield?.fontSize?.medium || "14px",
            lineHeight: theme.overwrites?.components?.textfield?.fontSize?.medium || "14px",
            height: theme.overwrites?.components?.textfield?.fontSize?.medium || "14px",

            // Size Small
            ...(size === "small" && {
                padding: "6px",
                fontSize: theme.overwrites?.components?.textfield?.fontSize?.small || "12px",
                lineHeight: theme.overwrites?.components?.textfield?.fontSize?.small || "12px",
                height: theme.overwrites?.components?.textfield?.fontSize?.small || "12px",
            }),

            // Size Large
            ...(size === "large" && {
                padding: hiddenLabel ? "11px 9px" : "16px 9px 6px",
                fontSize: theme.overwrites?.components?.textfield?.fontSize?.large || "14px",
                lineHeight: theme.overwrites?.components?.textfield?.fontSize?.large || "14px",
                height: theme.overwrites?.components?.textfield?.fontSize?.large || "14px",
            }),

            // Size Extralarge
            ...(size === "extralarge" && {
                padding: hiddenLabel ? "15px 11px" : "21px 11px 9px",
                fontSize: theme.overwrites?.components?.textfield?.fontSize?.extralarge || "16px",
                lineHeight: theme.overwrites?.components?.textfield?.fontSize?.extralarge || "16px",
                height: theme.overwrites?.components?.textfield?.fontSize?.extralarge || "16px",
            }),

            "&.MuiSelect-select": {
                height: "17px",
                minHeight: 0,
            },

            "&.MuiInputBase-inputMultiline": {
                padding: 0,
            },
        },

        // Variant Outline
        [`.${outlinedInputClasses.input}`]: {
            padding: "6px 11px",
        },

        // Filled Root
        [`.${filledInputClasses.root}`]: {
            border: `1px solid ${theme.overwrites?.components?.textfield?.border?.color || "#e2e2e1"}`,
            paddingRight: 8,
            borderRadius: theme.radius?.default || "3px",
            backgroundColor: theme.overwrites?.components?.textfield?.backgroundColor || colors.grey[100],
            transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),

            "&:hover": {
                backgroundColor: theme.overwrites?.components?.textfield?.backgroundColor || colors.grey[100],
            },
            [`&.${filledInputClasses.focused}`]: {
                backgroundColor: theme.overwrites?.components?.textfield?.backgroundColor || colors.grey[100],
                borderColor: theme.palette.primary.main,
                boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 2px`,
            },
            [`&.${filledInputClasses.error}`]: {
                borderColor: theme.palette.error.main,
                borderBottom: `4px solid ${theme.palette.error.main}`,
                marginBottom: "-3px", // prevents TextField height from being changed when error is shown
                [`&.${filledInputClasses.focused}`]: {
                    boxShadow: `${alpha(theme.palette.error.main, 0.25)} 0 0 0 2px`,
                },
            },
        },

        [`.${inputBaseClasses.root}`]: {
            "&:before, &:after": {
                // Instead of "disableUnderline"
                display: "none",
            },
        },

        // Reset blue-ish color when using autofill
        [`.${inputBaseClasses.input}`]: {
            ":-webkit-autofill": {
                WebkitBoxShadow: "0 0 0 30px white inset !important",
            },
        },

        [`.${inputBaseClasses.multiline}`]: {
            paddingTop: "14px",
            [`&.MuiInputBase-sizeSmall`]: {
                paddingTop: "6px",
            },
            ...(size === "large" && {
                paddingTop: "28px",
            }),
        },

        // Helper Text / Error Text
        [`.${formHelperTextClasses.root}`]: {
            [`&.${formHelperTextClasses.contained}`]: {
                [`&.${formHelperTextClasses.error}`]: {
                    [`&.${formHelperTextClasses.focused}`]: {
                        display: "none",
                    },
                    display: "none",
                    position: "absolute",
                    bottom: 0,
                    marginLeft: "0",
                    marginTop: "0",
                    borderRadius: `0 0 ${theme.radius?.default ?? "3px"} ${theme.radius?.default ?? "3px"}`,
                    color: theme.palette.error.contrastText,
                    backgroundColor: theme.palette.error.main,
                    width: "100%",
                    fontSize: "11px",
                    paddingLeft: "5px",
                    transform: "translateY(15px)",
                },
            },
        },

        ".MuiInputLabel-asterisk": {
            fontSize: "1.5em",
            verticalAlign: "sub",
            paddingLeft: 2,
            color: theme.palette.error.main,
        },
    }
}

export const StyledTooltip = styled(Tooltip)({
    [`.${tooltipClasses.tooltipPlacementBottom}`]: {
        marginTop: "8px !important",
        textAlign: "center",
    },
})
